import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Ansicht } from '@/features/reports/lib/accounting-period'

/**
 * The Buchhaltung Ansicht each user had last, per browser (CHG-2026-10-accounting-close-periods,
 * D7).
 *
 * The controller works from one browser and wants the report to open the way he left it; a shop
 * manager on the same browser keeps their own. So the store holds one entry per user, keyed by
 * {@link accountingUserKey}, and the page reads it only when the URL names no Ansicht — a link
 * always wins over a memory.
 */
interface AccountingAnsichtState {
  byUser: Partial<Record<string, Ansicht>>
  setAnsicht: (userKey: string, ansicht: Ansicht) => void
}

export const useAccountingAnsichtStore = create<AccountingAnsichtState>()(
  persist(
    (set) => ({
      byUser: {},
      setAnsicht: (userKey, ansicht) =>
        set((state) =>
          state.byUser[userKey] === ansicht
            ? state
            : { byUser: { ...state.byUser, [userKey]: ansicht } },
        ),
    }),
    { name: 'accounting-ansicht', version: 1 },
  ),
)

/**
 * Who the remembered Ansicht belongs to. The backoffice knows its user by e-mail and tenant, not by
 * a numeric id, and the pair is unique per login.
 */
export function accountingUserKey(
  user: { email: string; tenantId?: number } | null | undefined,
): string {
  return user ? `${user.tenantId ?? ''}:${user.email}` : ''
}
